import { useNavigate, useParams } from "react-router-dom"
import { useAppDispatch } from "../../../store/app/hooks"
import { useEffect, useState } from "react"
import { getErrorsAsString } from "../../../utils/getErrorsAsString"
import { setOpenSnackbar } from "../../../store/features/snackbar/snackbarSlice"
import { graphql } from "../../../gql"
import { useLazyQuery, useMutation } from "@apollo/client"
import { updateCacheCategory } from "../../../caches/updateCacheCategory"
import FormCategory from "../FormCategory/FormCategory"
import Loader from "../../Common/Loader/Loader"
import { InfinityListItem } from "../../../types/InfinityListItem"
import { isSuperAdmin } from "../../../utils/permissions"
import { getUserCountryId } from "../../../utils/authToken"
import useLang from "../../../hooks/useLang"

const editCategoryMutation = graphql(`
  mutation editCategoryMutation(
    $data: CategoryUpdateInput!
    $where: CategoryWhereUniqueInput!
  ) {
    updateOneCategory(data: $data, where: $where) {
      id
      name_fr
      name_en
      Segmentation {
        name_fr
        id
        name_en
      }
      Countries {
        id
        name_fr
      }
    }
  }
`)

const getOneCategoryQuery = graphql(`
  query getOneCategoryQuery($where: CategoryWhereUniqueInput!) {
    category(where: $where) {
      id
      name_fr
      name_en
      Segmentation {
        name_fr
        id
        name_en
      }
      Countries {
        id
        name_fr
      }
    }
  }
`)

export default function EditCategory() {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const params = useParams()
  const { getText } = useLang()
  const id = params.id ? parseInt(params.id, 10) : 0
  const [existingCountryIds, setExistingCountryIds] = useState<number[]>([])
  const [country, setCountry] = useState<InfinityListItem[]>([])

  const [category, setCategory] = useState({
    name_fr: "",
    name_en: "",
  })

  const [segmentation, setSegmentation] = useState<InfinityListItem>({
    label: "",
    value: "",
  })

  const getSegmentation = (value: InfinityListItem) => {
    setSegmentation(value)
  }

  const [getCategory, { loading: loadingInit, error }] = useLazyQuery(
    getOneCategoryQuery,
    {
      onCompleted(data) {
        const id = data?.category?.Segmentation?.id

        const countries = data.category?.Countries
        const countryIds = countries?.map((el) => el.id)

        if (!isSuperAdmin()) {
          if (countryIds) {
            if (!countryIds.includes(getUserCountryId())) {
              navigate("/")
              return
            }
          }
        }
        if (countries && countries.length) {
          setCountry([
            ...countries.map((el) => ({
              label: el.name_fr,
              value: el.id.toString(),
            })),
          ])
          setExistingCountryIds([...countries.map((el) => el.id)])
        }
        setCategory({
          name_en: data?.category?.name_en || "",
          name_fr: data?.category?.name_fr || "",
        })
        setSegmentation({
          label: data?.category?.Segmentation?.name_fr || "",
          value: id ? String(id) : "",
        })
      },
    },
  )

  const [updateCategory, { loading }] = useMutation(editCategoryMutation)

  const handleInputChange = (event: any) => {
    setCategory({
      ...category,
      [event.target.name]: event.target.value,
    })
  }

  const handleCreateCategory = (e: any) => {
    e.preventDefault()
    if (!country.length) {
      dispatch(
        setOpenSnackbar({
          message: getText("Veuillez préciser le pays, s'il vous plait."),
        }),
      )
      return
    }
    updateCategory({
      variables: {
        data: {
          name_en: {
            set: category.name_en,
          },
          name_fr: {
            set: category.name_fr,
          },
          Segmentation: {
            connect: { id: Number(segmentation.value) },
          },
          Countries: {
            ...(isSuperAdmin() && {
              disconnect: existingCountryIds.map((id) => ({ id })),
            }),
            connect: country.map((el) => ({ id: +el.value })),
          },
        },
        where: {
          id,
        },
      },
      onCompleted: () => {
        navigate("/category/list")
      },
      onError: (err) => {
        const message = getErrorsAsString(err)
        dispatch(setOpenSnackbar({ message }))
      },
      update: (cache, { data }) => {
        updateCacheCategory({
          action: "update",
          cache,
          entryData: data?.updateOneCategory,
        })
      },
    })
  }

  const getCountry = (value: InfinityListItem[]) => {
    if (value) {
      setCountry(value)
    } else {
      setCountry([])
    }
  }

  useEffect(() => {
    getCategory({
      variables: {
        where: {
          id,
        },
      },
    })
  }, [id])

  if (loadingInit) return <Loader />

  if (error) return "error"

  return (
    <FormCategory
      handleInputChange={handleInputChange}
      handleSubmit={handleCreateCategory}
      loading={loading}
      name_en={category.name_en}
      name_fr={category.name_fr}
      title={getText("Modifier une category")}
      getSegmentaion={getSegmentation}
      segmentation={segmentation}
      countryDefault={country}
      getCountry={getCountry}
    />
  )
}
